import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import KPICard from "@/components/KPICard";
import StatusBadge from "@/components/StatusBadge";
import { INR, INR_COMPACT, fmtDate, fmtRelative } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, ListChecks, IndianRupee, Briefcase, PackageCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";

const COLORS = ["hsl(15 68% 54%)", "hsl(25 80% 55%)", "hsl(200 30% 40%)", "hsl(45 90% 55%)", "hsl(160 40% 42%)", "hsl(280 60% 55%)"];

export default function Dashboard() {
  const [d, setD] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const financeOk = user?.finance_access || user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d) return <div className="text-sm text-muted-foreground">Loading dashboard…</div>;

  const k = d.kpis;
  const barData = (d.revenue_by_service || []).slice(0, 6);
  const pieData = Object.entries(d.task_statuses || {}).map(([name, value]) => ({ name, value })).filter((x) => x.value > 0);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header row */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Overview</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mt-1">Good to see you, {user?.name?.split(" ")[0] || "there"}.</h1>
          <p className="text-sm text-muted-foreground mt-1">{fmtDate(new Date().toISOString())} — Here's what's happening across your business.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="qa-add-client-btn" onClick={() => navigate("/clients?new=1")} variant="outline"><Plus size={14} className="mr-1" />Client</Button>
          <Button data-testid="qa-create-project-btn" onClick={() => navigate("/projects?new=1")} variant="outline"><Briefcase size={14} className="mr-1" />Project</Button>
          <Button data-testid="qa-add-task-btn" onClick={() => navigate("/tasks?new=1")} variant="outline"><ListChecks size={14} className="mr-1" />Task</Button>
          {financeOk && <Button data-testid="qa-record-payment-btn" onClick={() => navigate("/finance?new=1")} className="bg-primary hover:bg-primary/90"><IndianRupee size={14} className="mr-1" />Payment</Button>}
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
        <KPICard testid="kpi-total-clients" to="/clients" label="Total Clients" value={k.total_clients} hint={`${k.active_clients} active`} />
        <KPICard testid="kpi-new-clients" to="/clients?range=this-month" label="New This Month" value={k.new_this_month} />
        <KPICard testid="kpi-active-projects" to="/projects?status=In Progress" label="Active Projects" value={k.active_projects} />
        <KPICard testid="kpi-tasks-today" to="/tasks?due=today" label="Tasks Due Today" value={k.tasks_today} accent={k.tasks_today > 0} />
        <KPICard testid="kpi-tasks-week" to="/tasks?due=week" label="Due This Week" value={k.tasks_week} />
        <KPICard testid="kpi-tasks-overdue" to="/tasks?due=overdue" label="Overdue Tasks" value={k.tasks_overdue} accent={k.tasks_overdue > 0} hint={k.tasks_overdue > 0 ? "Needs attention" : "All on track"} />
        {financeOk && (
          <>
            <KPICard testid="kpi-contract-value" to="/finance" label="Contract Value" value={INR_COMPACT(k.contract_value)} />
            <KPICard testid="kpi-received" to="/finance?tab=payments" label="Received" value={INR_COMPACT(k.received)} />
            <KPICard testid="kpi-outstanding" to="/finance?tab=outstanding" label="Outstanding" value={INR_COMPACT(k.outstanding)} accent={k.outstanding > 0} />
            <KPICard testid="kpi-overdue-amt" to="/finance?tab=outstanding&filter=overdue" label="Overdue Amount" value={INR_COMPACT(k.overdue)} accent={k.overdue > 0} />
          </>
        )}
        <KPICard testid="kpi-upcoming-deliverables" to="/deliverables?range=upcoming" label="Upcoming Deliverables" value={k.upcoming_deliverables} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {financeOk && (
          <div className="lg:col-span-2 border border-border rounded-md p-5 bg-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Revenue</div>
                <h3 className="font-display text-lg font-extrabold">Monthly received</h3>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={d.revenue_by_month} margin={{ left: -10, right: 10, top: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => INR_COMPACT(v)} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => INR(v)}
                />
                <Line type="monotone" dataKey="amount" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="border border-border rounded-md p-5 bg-card">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tasks</div>
          <h3 className="font-display text-lg font-extrabold mb-2">By status</h3>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-muted-foreground">No task data yet</div>}
          <div className="flex flex-wrap gap-2 mt-2">
            {pieData.map((p, i) => (
              <span key={p.name} className="font-mono text-[10px] flex items-center gap-1">
                <span className="w-2 h-2 rounded" style={{ background: COLORS[i % COLORS.length] }} />
                {p.name} · {p.value}
              </span>
            ))}
          </div>
        </div>
      </div>

      {financeOk && barData.length > 0 && (
        <div className="border border-border rounded-md p-5 bg-card">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Services</div>
          <h3 className="font-display text-lg font-extrabold mb-4">Revenue by service</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="service" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => INR_COMPACT(v)} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                formatter={(v) => INR(v)}
              />
              <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Priorities + activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border rounded-md bg-card">
          <div className="p-4 border-b border-border">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Today</div>
            <h3 className="font-display text-lg font-extrabold">Priorities</h3>
          </div>
          <div className="divide-y divide-border max-h-80 overflow-y-auto">
            {(d.todays_priority || []).slice(0, 10).map((t) => (
              <div key={t.id} className="px-4 py-3 flex items-center justify-between hover:bg-muted/40 transition-colors" data-testid={`priority-task-${t.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">Due {fmtDate(t.due_date)}</div>
                </div>
                <StatusBadge value={t.status} />
              </div>
            ))}
            {(!d.todays_priority || d.todays_priority.length === 0) && (
              <div className="p-6 text-sm text-muted-foreground text-center">Nothing urgent today. Well done!</div>
            )}
          </div>
        </div>

        <div className="border border-border rounded-md bg-card">
          <div className="p-4 border-b border-border">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Recent</div>
            <h3 className="font-display text-lg font-extrabold">Activity</h3>
          </div>
          <div className="divide-y divide-border max-h-80 overflow-y-auto">
            {(d.activity || []).slice(0, 12).map((a) => (
              <div key={a.id} className="px-4 py-3 text-sm" data-testid={`activity-${a.id}`}>
                <div><span className="font-semibold">{a.actor_name}</span> <span className="text-muted-foreground">{a.action.replace(".", " ")}</span> <span className="font-medium">{a.entity_label}</span></div>
                <div className="text-[11px] font-mono text-muted-foreground">{fmtRelative(a.created_at)}</div>
              </div>
            ))}
            {(!d.activity || d.activity.length === 0) && <div className="p-6 text-sm text-muted-foreground text-center">No recent activity.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
